import React from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  ArrowLeft,
  AlertTriangle,
  PackagePlus,
  Stethoscope,
  BarChart3,
  Coins,
  CheckCircle2,
  CalendarClock,
} from "lucide-react";
import useInventoryOwner from "../../hooks/useInventoryOwner";
import useBrandTranslation from "../../hooks/useBrandTranslation";
import useInventorySettings from "../../hooks/useInventorySettings";
import { fetchReorderSupplies, fetchExpiringSupplies } from "../../apis/api.inventory";
import { queryInventoryReorderKey, queryInventoryExpiringKey } from "../../apis/queryKeys";
import { formatAmount } from "../../utils/inventoryFormat";
import { formatDateOnly, todayISODate } from "../../utils/inventoryExpiration";
import Button from "../common/Button";
import Spinner from "../common/Spinner";
import SupplyList from "./SupplyList";
import InventoryCharts from "./InventoryCharts";
import InventoryOwnerSelector from "./InventoryOwnerSelector";

function QuickAction({ icon: Icon, label, onClick, color }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex flex-col items-center justify-center gap-2 rounded-lg border border-gray-200 bg-white p-4 shadow-sm hover:shadow-md hover:border-gray-300 transition-all"
    >
      <Icon className={`w-7 h-7 ${color}`} />
      <span className="text-xs font-medium text-gray-700 text-center">{label}</span>
    </button>
  );
}

export default function InventoryHome() {
  const { t } = useBrandTranslation();
  const { ownerId, isSecretary, isLoading: loadingOwners } = useInventoryOwner();
  const navigate = useNavigate();

  const { isConfigured, decimalSeparator, isLoading: loadingSettings } =
    useInventorySettings(ownerId);

  const {
    data: reorder,
    isLoading: loadingReorder,
    error: reorderError,
  } = useQuery({
    queryKey: queryInventoryReorderKey(ownerId),
    queryFn: () => fetchReorderSupplies(ownerId),
    enabled: !!ownerId,
    retry: false,
  });

  const today = todayISODate();
  const { data: expiring, isLoading: loadingExpiring } = useQuery({
    queryKey: queryInventoryExpiringKey(ownerId, today),
    queryFn: () => fetchExpiringSupplies(ownerId, today),
    enabled: !!ownerId && !reorderError,
    retry: false,
  });

  // Secretaria sin profesionales, institución sin profesionales asociados u otro bloqueo del backend.
  const blockedMessage =
    isSecretary && !loadingOwners && !ownerId
      ? t(
          "inventory.owner.noProfessionals",
          "Todavía no trabajás con ningún profesional, así que no hay inventarios para ver."
        )
      : reorderError?.message;
  if (blockedMessage) {
    return (
      <div className="min-h-screen bg-gray-50 p-4">
        <div className="max-w-3xl mx-auto">
          <Button variant="outline" onClick={() => navigate("/")} className="mb-4">
            <ArrowLeft className="w-4 h-4 mr-2" />
            {t("inventory.back", "Volver")}
          </Button>
          <div className="rounded-lg border border-amber-300 bg-amber-50 px-4 py-6 text-center text-amber-900">
            <AlertTriangle className="w-8 h-8 mx-auto mb-2" />
            <p className="text-sm">{blockedMessage}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-4">
      <div className="max-w-3xl mx-auto space-y-5 pb-24">
        <div>
          <Button variant="outline" onClick={() => navigate("/")} className="mb-3">
            <ArrowLeft className="w-4 h-4 mr-2" />
            {t("inventory.back", "Volver")}
          </Button>
          <h1 className="text-2xl font-bold text-gray-900">{t("inventory.title", "Inventario")}</h1>
          <p className="text-sm text-gray-500">
            {t("inventory.subtitle", "Gestioná tus insumos, tratamientos y su consumo.")}
          </p>
        </div>

        <InventoryOwnerSelector />

        {/* Alerta de configuración de Monedas */}
        {!loadingSettings && !isConfigured && (
          <div className="rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900 flex items-start gap-2">
            <AlertTriangle className="w-5 h-5 shrink-0 mt-0.5" />
            <div className="flex-1">
              <p>
                {isSecretary
                  ? t(
                      "inventory.currencyAlertSecretary",
                      'El profesional todavía no completó el apartado de "Monedas" en su perfil.'
                    )
                  : t(
                      "inventory.currencyAlert",
                      'Para poder utilizarlo correctamente, recomendamos completar el apartado de "Monedas".'
                    )}
              </p>
              {!isSecretary && (
              <button
                type="button"
                onClick={() => navigate("/profile?tab=currencies")}
                className="mt-1 text-amber-800 font-semibold underline"
              >
                {t("inventory.goToCurrencies", "Ir a Monedas")}
              </button>
              )}
            </div>
          </div>
        )}

        {/* Accesos rápidos */}
        <div className={`grid grid-cols-2 gap-3 ${isSecretary ? "sm:grid-cols-3" : "sm:grid-cols-4"}`}>
          <QuickAction
            icon={PackagePlus}
            color="text-blue-600"
            label={t("inventory.nav.newSupply", "+ Insumo")}
            onClick={() => navigate("/inventory/supplies/new")}
          />
          <QuickAction
            icon={Stethoscope}
            color="text-green-600"
            label={t("inventory.nav.treatments", "Tratamientos")}
            onClick={() => navigate("/inventory/treatments")}
          />
          <QuickAction
            icon={BarChart3}
            color="text-purple-600"
            label={t("inventory.nav.charts", "Gráficos")}
            onClick={() => {
              document.getElementById("inventory-charts")?.scrollIntoView({ behavior: "smooth" });
            }}
          />
          {/* Monedas es configuración del profesional: la secretaria no la edita. */}
          {!isSecretary && (
            <QuickAction
              icon={Coins}
              color="text-amber-500"
              label={t("inventory.nav.currencies", "Monedas")}
              onClick={() => navigate("/profile?tab=currencies")}
            />
          )}
        </div>

        {/* Insumos a reponer */}
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <p className="text-sm font-semibold text-gray-800 mb-3">
            {t("inventory.reorderTitle", "Insumos a reponer")}
          </p>
          {loadingReorder ? (
            <Spinner className="w-6 h-6 mx-auto my-3" />
          ) : !reorder || reorder.length === 0 ? (
            <div className="flex items-center gap-2 text-green-700 bg-green-50 border border-green-200 rounded-md px-3 py-2 text-sm">
              <CheckCircle2 className="w-5 h-5" />
              {t("inventory.reorderEmpty", "Stock en condiciones normales")}
            </div>
          ) : (
            <div>
              <p className="text-xs text-gray-500 mb-2">
                {t("inventory.reorderHint", "Estos insumos alcanzaron o superaron su quiebre de stock.")}
              </p>
              <ul className="space-y-1">
                {reorder.map((s) => (
                  <li
                    key={s.id}
                    className="flex items-center justify-between rounded-md bg-red-50 border border-red-200 px-3 py-2"
                  >
                    <span className="text-sm font-medium text-red-800">{s.description}</span>
                    <span className="text-xs font-semibold text-red-700">
                      {t("inventory.list.stock", "Stock")}: {formatAmount(s.stock, decimalSeparator)}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        {/* Insumos por vencer */}
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <p className="text-sm font-semibold text-gray-800 mb-3">
            {t("inventory.expiringTitle", "Insumos por vencer")}
          </p>
          {loadingExpiring ? (
            <Spinner className="w-6 h-6 mx-auto my-3" />
          ) : !expiring || expiring.length === 0 ? (
            <div className="flex items-center gap-2 text-green-700 bg-green-50 border border-green-200 rounded-md px-3 py-2 text-sm">
              <CheckCircle2 className="w-5 h-5" />
              {t("inventory.expiringEmpty", "No hay insumos próximos a vencer")}
            </div>
          ) : (
            <div>
              <p className="text-xs text-gray-500 mb-2">
                {t(
                  "inventory.expiringHint",
                  "Estos insumos alcanzaron su fecha de alerta por vencimiento o ya vencieron."
                )}
              </p>
              <ul className="space-y-1">
                {expiring.map((s) => (
                  <li
                    key={s.id}
                    className={`flex items-center justify-between gap-2 rounded-md border px-3 py-2 ${
                      s.expired ? "bg-red-50 border-red-200" : "bg-amber-50 border-amber-200"
                    }`}
                  >
                    <span
                      className={`flex items-center gap-2 text-sm font-medium ${
                        s.expired ? "text-red-800" : "text-amber-900"
                      }`}
                    >
                      <CalendarClock className="w-4 h-4 shrink-0" />
                      {s.description}
                    </span>
                    <span
                      className={`text-xs font-semibold whitespace-nowrap ${
                        s.expired ? "text-red-700" : "text-amber-800"
                      }`}
                    >
                      {s.expired
                        ? t("inventory.expiredOn", "Venció el {{date}}", {
                            date: formatDateOnly(s.expiration_date),
                          })
                        : t("inventory.expiresOn", "Vence el {{date}}", {
                            date: formatDateOnly(s.expiration_date),
                          })}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        {/* Listado de insumos */}
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <p className="text-sm font-semibold text-gray-800 mb-3">
            {t("inventory.nav.suppliesList", "Listado Insumos")}
          </p>
          <SupplyList />
        </div>

        {/* Gráficos */}
        <div id="inventory-charts">
          <p className="text-sm font-semibold text-gray-800 mb-3">
            {t("inventory.charts.title", "Gráficos")}
          </p>
          <InventoryCharts />
        </div>
      </div>
    </div>
  );
}
